// src/dnd/views/Sidebar_parts/CharactersTab.js
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useParams } from "react-router-dom";
import CharacterUploadForm from "./character_parts/CharacterUploadForm";
import CharacterSheetPreview from "./character_parts/CharacterSheetPreview";

function pick(obj, keys) {
  for (const k of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
  }
  return undefined;
}

function normalizeApiBase(base) {
  if (base == null) return "";
  const s = String(base || "").trim();
  if (!s) return "";
  return s.endsWith("/") ? s.slice(0, -1) : s;
}

function apiUrl(apiBase, path) {
  const base = normalizeApiBase(apiBase);
  const p = String(path || "");
  if (!p) return base || "";
  if (!p.startsWith("/")) return `${base}/${p}`;
  return `${base}${p}`;
}

async function fetchJsonWithStatus(apiBase, path, options) {
  const res = await fetch(apiUrl(apiBase, path), {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options && options.headers ? options.headers : {}),
    },
  });

  let json = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }

  return { ok: res.ok, status: res.status, json };
}

function buildHttpError(status, json) {
  const msg = (json && (json.error || json.message)) || `Request failed (${status})`;
  const detail = json && json.detail ? `: ${json.detail}` : "";
  const err = new Error(`${msg}${detail}`);
  err.status = status;
  err.payload = json;
  return err;
}

async function apiPostJsonWithFallback(apiBase, paths, bodyObj) {
  const payload = JSON.stringify(bodyObj);
  let lastNon404 = null;

  for (const p of paths) {
    let r;
    try {
      r = await fetchJsonWithStatus(apiBase, p, {
        method: "POST",
        body: payload,
      });
    } catch (e) {
      throw new Error(String(e && e.message ? e.message : e));
    }

    if (r.ok) return { path: p, json: r.json };

    if (r.status === 404) continue;

    lastNon404 = buildHttpError(r.status, r.json);
    break;
  }

  if (lastNon404) throw lastNon404;

  const err = new Error(`Save failed: endpoint not found. Tried: ${paths.join(", ")}`);
  err.status = 404;
  throw err;
}

async function apiGetJsonWithFallback(apiBase, paths) {
  let lastNon404 = null;

  for (const p of paths) {
    let r;
    try {
      r = await fetchJsonWithStatus(apiBase, p, { method: "GET" });
    } catch (e) {
      throw new Error(String(e && e.message ? e.message : e));
    }

    if (r.ok) return { path: p, json: r.json };

    if (r.status === 404) continue;

    lastNon404 = buildHttpError(r.status, r.json);
    break;
  }

  if (lastNon404) throw lastNon404;

  const err = new Error(`Load failed: endpoint not found. Tried: ${paths.join(", ")}`);
  err.status = 404;
  throw err;
}

async function sha256Hex(text) {
  try {
    if (typeof window === "undefined" || !window.crypto || !window.crypto.subtle) {
      return null;
    }
    const enc = new TextEncoder();
    const buf = enc.encode(String(text || ""));
    const digest = await window.crypto.subtle.digest("SHA-256", buf);
    const bytes = new Uint8Array(digest);
    let out = "";
    for (let i = 0; i < bytes.length; i += 1) {
      out += bytes[i].toString(16).padStart(2, "0");
    }
    return out;
  } catch {
    return null;
  }
}

function readMapIdFromSearch(search) {
  try {
    const sp = new URLSearchParams(String(search || ""));
    const q = sp.get("map_id") || sp.get("mapId") || sp.get("id") || sp.get("map") || "";
    if (q && String(q).trim()) return String(q).trim();
  } catch {}
  return "";
}

function readMapIdFromPathname(pathname) {
  const p = String(pathname || "");
  if (!p) return "";
  const patterns = [
    /\/maps\/(\d+)(?:\/|$)/i,
    /\/map\/(\d+)(?:\/|$)/i,
    /\/viewer\/(\d+)(?:\/|$)/i,
    /\/dnd\/(\d+)(?:\/|$)/i,
  ];
  for (const re of patterns) {
    const m = p.match(re);
    if (m && m[1]) return String(m[1]);
  }
  return "";
}

function safeJsonParse(maybeJson) {
  if (maybeJson == null) return null;
  if (typeof maybeJson === "object") return maybeJson;
  if (typeof maybeJson !== "string") return null;
  const s = maybeJson.trim();
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function normalizeServerSheetToCharacter(sheet) {
  const parsed =
    (sheet && sheet.data != null ? safeJsonParse(sheet.data) : null) ||
    (sheet && sheet.raw_json != null ? safeJsonParse(sheet.raw_json) : null) ||
    null;

  const name =
    (sheet && sheet.name) ||
    (parsed && parsed.name) ||
    (parsed && parsed.actor && parsed.actor.name) ||
    null;

  return {
    id: sheet && sheet.id != null ? sheet.id : undefined,
    server_id: sheet && sheet.id != null ? sheet.id : null,
    map_id: sheet && sheet.map_id != null ? sheet.map_id : null,
    token_id: sheet && sheet.token_id != null ? sheet.token_id : null,
    name,
    level: sheet && sheet.level != null ? sheet.level : null,
    class_primary: sheet && sheet.class_primary != null ? sheet.class_primary : null,
    race: sheet && sheet.race != null ? sheet.race : null,
    background: sheet && sheet.background != null ? sheet.background : null,
    alignment: sheet && sheet.alignment != null ? sheet.alignment : null,
    hp_current: sheet && sheet.hp_current != null ? sheet.hp_current : null,
    hp_max: sheet && sheet.hp_max != null ? sheet.hp_max : null,
    ac: sheet && sheet.ac != null ? sheet.ac : null,
    speed_walk: sheet && sheet.speed_walk != null ? sheet.speed_walk : null,
    updated_at: sheet && sheet.updated_at ? sheet.updated_at : null,
    created_at: sheet && sheet.created_at ? sheet.created_at : null,
    parsed,
    raw: sheet && sheet.raw_text ? { type: "text", text: String(sheet.raw_text) } : null,
    normalized: { name: name || null },
    savedSheet: sheet || null,
  };
}

function mergeUniqueCharacters(listA, listB) {
  const a = Array.isArray(listA) ? listA : [];
  const b = Array.isArray(listB) ? listB : [];
  const seen = new Set();
  const out = [];

  function keyFor(c, idx) {
    const k =
      (c && (c.id != null ? `id:${c.id}` : "")) ||
      (c && (c.server_id != null ? `sid:${c.server_id}` : "")) ||
      (c && (c._id != null ? `oid:${c._id}` : "")) ||
      (c && (c.uuid != null ? `uuid:${c.uuid}` : "")) ||
      (c && (c.name ? `name:${String(c.name)}` : "")) ||
      `idx:${idx}`;
    return k;
  }

  const combined = [...a, ...b];
  for (let i = 0; i < combined.length; i += 1) {
    const c = combined[i];
    const k = keyFor(c, i);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(c);
  }
  return out;
}

export default function CharactersTab(props) {
  const {
    TIP_BAR_HEIGHT,
    isMobile,
    characters = [],
    onCreateCharacter = () => {},
    onUploadCharacters = () => {},
    onEditCharacter = null,
  } = props;

  const params = useParams();
  const location = useLocation();

  const [showUpload, setShowUpload] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  const [serverCharacters, setServerCharacters] = useState([]);
  const [loadingServer, setLoadingServer] = useState(false);
  const [serverErr, setServerErr] = useState("");

  const mergedCharacters = useMemo(() => {
    const local = Array.isArray(characters) ? characters : [];
    const server = Array.isArray(serverCharacters) ? serverCharacters : [];
    const merged = mergeUniqueCharacters(local, server);
    return merged;
  }, [characters, serverCharacters]);

  const isBlank = !Array.isArray(mergedCharacters) || mergedCharacters.length === 0;

  const activeCharacter = useMemo(() => {
    if (!Array.isArray(mergedCharacters) || mergedCharacters.length === 0) return null;
    const idx = Math.max(0, Math.min(mergedCharacters.length - 1, Number(selectedIdx) || 0));
    return mergedCharacters[idx] || mergedCharacters[0] || null;
  }, [mergedCharacters, selectedIdx]);

  const buttonStyle = {
    appearance: "none",
    background: "#2b2c31",
    border: "1px solid #3a3b40",
    color: "#eee",
    padding: "10px 14px",
    borderRadius: 12,
    fontWeight: 800,
    cursor: "pointer",
  };

  const headerButtonStyle = {
    ...buttonStyle,
    padding: "8px 12px",
    borderRadius: 12,
    fontWeight: 900,
  };

  const overlayStyle = useMemo(() => {
    const bottomPad = isMobile ? (TIP_BAR_HEIGHT || 0) : 0;
    return {
      position: "fixed",
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 999999,
      background: "#000a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 12,
      paddingBottom: 12 + bottomPad,
    };
  }, [isMobile, TIP_BAR_HEIGHT]);

  const modalWrapStyle = {
    borderRadius: 16,
    boxShadow: "0 20px 70px #000c",
  };

  function openUpload(e) {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setShowUpload(true);
  }

  function closeUpload(e) {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setShowUpload(false);
  }

  function resolveMapId() {
    const v = pick(props, ["map_id", "mapId", "currentMapId", "activeMapId", "mapID"]);
    if (v != null && String(v) !== "") return String(v);

    const m = pick(props, ["map", "activeMap", "currentMap"]);
    if (m && (m.id != null || m.map_id != null)) {
      const id = m.id != null ? m.id : m.map_id;
      if (id != null && String(id) !== "") return String(id);
    }

    const pv = pick(params || {}, ["mapId", "map_id", "id", "map", "slug"]);
    if (pv != null && String(pv).trim()) {
      const s = String(pv).trim();
      const asNum = s.match(/^(\d+)$/) ? s : "";
      if (asNum) return asNum;
    }

    const fromSearch = readMapIdFromSearch(location && location.search ? location.search : "");
    if (fromSearch) return fromSearch;

    const fromPath = readMapIdFromPathname(location && location.pathname ? location.pathname : "");
    if (fromPath) return fromPath;

    return "";
  }

  function resolveTokenId() {
    const v = pick(props, ["token_id", "tokenId", "activeTokenId"]);
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.trunc(n);
  }

  function resolveApiBase() {
    const v = pick(props, ["apiBase", "API_BASE", "api_base"]);
    if (v != null) return String(v || "");
    return "";
  }

  function resolvePlayerSheetsPostPaths() {
    const v = pick(props, ["playerSheetsPostPaths", "playerSheetsPaths"]);
    if (Array.isArray(v) && v.length) {
      return v.map((x) => String(x)).filter(Boolean);
    }
    return [
      "/api/player-sheets",
      "/api/playerSheets",
      "/api/player_sheets",
      "/api/dnd/player-sheets",
      "/api/dnd/playerSheets",
    ];
  }

  function resolvePlayerSheetsGetPaths(mapId, tokenId) {
    const qp = new URLSearchParams();
    if (mapId) qp.set("map_id", String(mapId));
    if (tokenId != null) qp.set("token_id", String(tokenId));
    qp.set("with_data", "1");
    qp.set("limit", "50");
    const qs = qp.toString();
    const suffix = qs ? `?${qs}` : "";
    return [
      `/api/player-sheets${suffix}`,
      `/api/playerSheets${suffix}`,
      `/api/player_sheets${suffix}`,
      `/api/dnd/player-sheets${suffix}`,
      `/api/dnd/playerSheets${suffix}`,
      `/api/tabletop/player-sheets${suffix}`,
    ];
  }

  async function saveToServer(previewPayload) {
    const mapId = resolveMapId();
    if (!mapId) {
      throw new Error(
        "No map_id available. Pass mapId/map_id to CharactersTab OR ensure the current URL contains the map id."
      );
    }

    const tokenId = resolveTokenId();
    const apiBase = resolveApiBase();
    const postPaths = resolvePlayerSheetsPostPaths();

    const parsed = previewPayload && previewPayload.parsed ? previewPayload.parsed : null;
    const raw = previewPayload && previewPayload.raw ? previewPayload.raw : null;

    const rawText =
      raw && typeof raw === "object" && typeof raw.text === "string" ? raw.text : null;

    const isJsonText = raw && typeof raw === "object" && raw.type === "json-text";
    const isText = raw && typeof raw === "object" && raw.type === "text";

    const sha = rawText && rawText.length && (isJsonText || isText) ? await sha256Hex(rawText) : null;

    const body = {
      map_id: mapId,
      token_id: tokenId,
      game_system: "dnd5e",
      source_app: "pointatlas",
      schema_version: "pointatlas.character.v1",
      status: "saved",
      change_note: "upload",
      data: parsed && typeof parsed === "object" ? parsed : null,
      raw_json: parsed && typeof parsed === "object" ? parsed : null,
      raw_text: rawText && rawText.length ? rawText : null,
      import: {
        detected_kind: previewPayload && previewPayload.detected ? String(previewPayload.detected) : null,
        original_name:
          previewPayload && previewPayload.file && previewPayload.file.name
            ? String(previewPayload.file.name)
            : null,
        mime:
          previewPayload && previewPayload.file && previewPayload.file.type
            ? String(previewPayload.file.type)
            : null,
        size_bytes:
          previewPayload && previewPayload.file && previewPayload.file.size != null
            ? Number(previewPayload.file.size)
            : null,
        sha256: sha,
        asset_id: null,
        stored_path: null,
        import_notes: null,
      },
    };

    const r = await apiPostJsonWithFallback(apiBase, postPaths, body);

    if (!r || !r.json || !r.json.success || !r.json.sheet) {
      throw new Error("Save failed: unexpected response from server");
    }

    return r.json.sheet;
  }

  async function handleSave(previewPayload) {
    const sheet = await saveToServer(previewPayload);

    const merged = {
      ...(previewPayload || {}),
      savedSheet: sheet,
      id: sheet && sheet.id != null ? sheet.id : previewPayload && previewPayload.id,
      server_id: sheet && sheet.id != null ? sheet.id : null,
      map_id: sheet && sheet.map_id != null ? sheet.map_id : null,
      token_id: sheet && sheet.token_id != null ? sheet.token_id : null,
      name:
        (sheet && sheet.name) ||
        (previewPayload && previewPayload.parsed && previewPayload.parsed.name) ||
        (previewPayload && previewPayload.normalized && previewPayload.normalized.name) ||
        (previewPayload && previewPayload.file && previewPayload.file.name) ||
        null,
      level: sheet && sheet.level != null ? sheet.level : null,
      class_primary: sheet && sheet.class_primary != null ? sheet.class_primary : null,
      race: sheet && sheet.race != null ? sheet.race : null,
      background: sheet && sheet.background != null ? sheet.background : null,
      alignment: sheet && sheet.alignment != null ? sheet.alignment : null,
      hp_current: sheet && sheet.hp_current != null ? sheet.hp_current : null,
      hp_max: sheet && sheet.hp_max != null ? sheet.hp_max : null,
      ac: sheet && sheet.ac != null ? sheet.ac : null,
      speed_walk: sheet && sheet.speed_walk != null ? sheet.speed_walk : null,
      updated_at: sheet && sheet.updated_at ? sheet.updated_at : null,
      created_at: sheet && sheet.created_at ? sheet.created_at : null,
    };

    onUploadCharacters(merged);

    try {
      const normalized = normalizeServerSheetToCharacter(sheet);
      setServerCharacters((prev) => mergeUniqueCharacters(prev, [normalized]));
      setSelectedIdx(0);
    } catch {}

    closeUpload();
    return true;
  }

  async function loadFromServer() {
    const mapId = resolveMapId();
    if (!mapId) return;

    const tokenId = resolveTokenId();
    const apiBase = resolveApiBase();
    const getPaths = resolvePlayerSheetsGetPaths(mapId, tokenId);

    setLoadingServer(true);
    setServerErr("");

    try {
      const r = await apiGetJsonWithFallback(apiBase, getPaths);
      const items = r && r.json && Array.isArray(r.json.items) ? r.json.items : [];
      const normalized = items.map((s) => normalizeServerSheetToCharacter(s));
      setServerCharacters(normalized);
      if (Number(selectedIdx) >= normalized.length && normalized.length) setSelectedIdx(0);
    } catch (e) {
      setServerErr(String(e && e.message ? e.message : e));
    } finally {
      setLoadingServer(false);
    }
  }

  useEffect(() => {
    if (!showUpload) return;
    function onKeyDown(ev) {
      if (ev.key === "Escape") closeUpload();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showUpload]);

  useEffect(() => {
    loadFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location && location.pathname ? location.pathname : "",
    location && location.search ? location.search : "",
  ]);

  useEffect(() => {
    if (!activeCharacter) return;
    const idx = Math.max(
      0,
      Math.min((mergedCharacters || []).length - 1, Number(selectedIdx) || 0)
    );
    if (idx !== Number(selectedIdx)) setSelectedIdx(idx);
  }, [activeCharacter, mergedCharacters, selectedIdx]);

  const modalNode =
    showUpload && typeof document !== "undefined"
      ? createPortal(
          <div style={overlayStyle} onMouseDown={closeUpload}>
            <div style={modalWrapStyle} onMouseDown={(e) => e.stopPropagation()}>
              <CharacterUploadForm
                title="Upload character"
                onSave={handleSave}
                onCancel={closeUpload}
              />
            </div>
          </div>,
          document.body
        )
      : null;

  function handleEditClick() {
    if (!activeCharacter) return;
    if (typeof onEditCharacter === "function") {
      onEditCharacter(activeCharacter);
      return;
    }
    openUpload();
  }

  const topBarStyle = {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 12,
  };

  const nameChipStyle = {
    flex: 1,
    minWidth: 0,
    background: "#14161a",
    border: "1px solid #2a2d33",
    borderRadius: 14,
    padding: "10px 12px",
    fontWeight: 900,
    color: "#e7e7ea",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
      {isBlank ? (
        <div
          style={{
            minHeight: 260,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          <div style={{ opacity: 0.85, marginBottom: 10, textAlign: "center" }}>
            No character sheets yet.
          </div>

          <button style={{ ...buttonStyle, width: "100%", maxWidth: 320 }} onClick={onCreateCharacter}>
            Create
          </button>

          <button style={{ ...buttonStyle, width: "100%", maxWidth: 320 }} onClick={openUpload}>
            Upload
          </button>

          {loadingServer ? (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>Loading from server…</div>
          ) : null}
          {serverErr ? (
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8, maxWidth: 360, textAlign: "center" }}>
              {serverErr}
            </div>
          ) : null}
        </div>
      ) : (
        <div>
          <div style={topBarStyle}>
            <div style={nameChipStyle}>
              {activeCharacter?.name || activeCharacter?.displayName || "Unnamed Character"}
            </div>

            <button style={headerButtonStyle} onClick={openUpload}>
              Upload
            </button>

            <button
              style={{
                ...headerButtonStyle,
                borderColor: "#5b2b2b",
                boxShadow: "0 0 0 1px #5b2b2b inset",
              }}
              onClick={handleEditClick}
            >
              Edit
            </button>
          </div>

          {mergedCharacters.length > 1 ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {mergedCharacters.map((c, idx) => {
                const isActive = idx === (Number(selectedIdx) || 0);
                return (
                  <button
                    key={c?.id || c?._id || c?.uuid || c?.server_id || idx}
                    onClick={() => setSelectedIdx(idx)}
                    style={{
                      appearance: "none",
                      cursor: "pointer",
                      borderRadius: 999,
                      padding: "7px 10px",
                      fontWeight: 900,
                      fontSize: 12,
                      border: "1px solid " + (isActive ? "#5b2b2b" : "#2a2d33"),
                      background: isActive ? "#2a0f12" : "#0d0e10",
                      color: isActive ? "#ffd6d9" : "#d7d9de",
                    }}
                  >
                    {c?.name || "Unnamed"}
                  </button>
                );
              })}
            </div>
          ) : null}

          {loadingServer ? (
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>Loading from server…</div>
          ) : null}
          {serverErr ? (
            <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>{serverErr}</div>
          ) : null}

          {activeCharacter ? <CharacterSheetPreview preview={activeCharacter} /> : null}
        </div>
      )}

      {modalNode}
    </div>
  );
}
